import React from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, GraduationCap } from "lucide-react";
import { Button } from "../components/ui/button";
import { useApplyModal } from "../lib/applyModal";

const navItems = [
  { label: "Home", to: "/" },
  {
    label: "About Us",
    children: [
      { label: "About Kiki Centre", to: "/about" },
      { label: "Partners", to: "/about/partners" },
      { label: "Certifications", to: "/about/certifications" },
      { label: "News & Events", to: "/news" },
    ],
  },
  {
    label: "Courses",
    children: [
      { label: "All Courses", to: "/courses" },
      { label: "Long Term Courses", to: "/courses/long-term" },
      { label: "Earn While You Learn", to: "/courses/earn-while-you-learn" },
      { label: "Short Term Courses", to: "/courses/short-term" },
      { label: "Corporate Training", to: "/courses/corporate" },
    ],
  },
  {
    label: "Student Life",
    children: [
      { label: "Student Life", to: "/student-life" },
      { label: "Activities", to: "/activities" },
      { label: "Residential Life", to: "/residential-life" },
      { label: "Life in Millennium City", to: "/life-in-the-millennium-city" },
      { label: "Facilities", to: "/facilities" },
      { label: "Student Speak", to: "/student-speak" },
    ],
  },
  { label: "Financial Aid", to: "/financial-aid" },
  { label: "Placement", to: "/placement" },
  { label: "Blog", to: "/blog" },
  { label: "Admissions", to: "/admissions" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const { openApply } = useApplyModal();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-9 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur shadow-md"
          : "bg-white/80 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <img
              src="/assets/logo.png"
              alt="Kiki Centre for Technology"
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) =>
              item.children ? (
                <DropdownItem key={item.label} item={item} />
              ) : (
                <NavLink
                  key={item.label}
                  to={item.to}
                  className={({ isActive }) =>
                    `relative px-3 py-2 text-sm font-semibold transition-colors ${
                      isActive
                        ? "text-[#0B2A6B]"
                        : "text-slate-700 hover:text-[#0B2A6B]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {item.label}
                      <span
                        className={`absolute -bottom-0.5 left-3 right-3 h-0.5 bg-yellow-400 transition-all ${
                          isActive ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              )
            )}
          </nav>

          {/* Call CTA + mobile toggle */}
          <div className="flex items-center gap-3">
            <a
              href="tel:+918800288994"
              className="hidden md:inline-flex items-center gap-2 text-sm font-bold text-[#0B2A6B] hover:text-yellow-500 transition"
            >
              <Phone className="h-4 w-4" />
              8800288994
            </a>
            <Button
              onClick={openApply}
              data-testid="navbar-apply-now"
              className="hidden md:inline-flex bg-yellow-400 hover:bg-yellow-500 text-[#0B2A6B] font-bold border-0"
            >
              Apply Now
            </Button>
            <button
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-[#0B2A6B]"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            {navItems.map((item) =>
              item.children ? (
                <details key={item.label} className="group">
                  <summary className="flex items-center justify-between py-3 px-2 text-sm font-semibold text-slate-700 cursor-pointer rounded hover:bg-slate-50">
                    {item.label}
                    <ChevronDown className="h-4 w-4 group-open:rotate-180 transition" />
                  </summary>
                  <div className="pl-4 pb-2 space-y-1">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        onClick={() => setOpen(false)}
                        className="block py-2 px-2 text-sm text-slate-600 hover:text-[#0B2A6B] hover:bg-yellow-50 rounded"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </details>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="block py-3 px-2 text-sm font-semibold text-slate-700 hover:text-[#0B2A6B] hover:bg-yellow-50 rounded"
                >
                  {item.label}
                </Link>
              )
            )}
            <a
              href="tel:+918800288994"
              className="flex items-center gap-2 py-3 px-2 text-sm font-bold text-[#0B2A6B]"
            >
              <Phone className="h-4 w-4" /> 8800288994
            </a>
            <Button
              onClick={() => {
                setOpen(false);
                openApply();
              }}
              data-testid="navbar-apply-now-mobile"
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-[#0B2A6B] font-bold border-0 mt-2"
            >
              Apply Now
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}

function DropdownItem({ item }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button className="flex items-center gap-1 px-3 py-2 text-sm font-semibold text-slate-700 hover:text-[#0B2A6B] transition">
        {item.label}
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      <div
        className={`absolute left-0 top-full pt-2 min-w-[230px] transition-all duration-200 z-50 ${
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
      >
        <div className="bg-white rounded-xl shadow-xl border border-slate-100 py-2 overflow-hidden">
          {item.children.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className="block px-4 py-2.5 text-sm font-medium text-slate-700 hover:text-[#0B2A6B] hover:bg-yellow-50 transition"
            >
              {c.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
